import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AudioAvatarConfig } from "@models/avatar-config";
import { RootState } from "@store/store";
import Loader from "@components/Loader/Loader";
import { useRecordVoice } from "@hooks/useRecordVoice";
import styled from "@emotion/styled";
import { setInitializing, setReady, setTalking } from "@store/avatarSlice";
import { useCompanionStreamingService } from "@hooks/useCompanionStreamingService";
import { AudioAvatarService } from "@service/avatar/audio-avatar.service";
import { Box } from "@mui/material";
import { useDebounceProcessing } from "@hooks/useDebounceProcessing";
import { useSendAudioMessageMutation } from "@store/companionApi";
import { EventType } from "@models/audio-message";

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  position: relative;
  height: 100%;
  justify-content: center;
  color: white;
`;

const AvatarImage = styled.img`
  max-width: 300px;
  width: 100%;
`;

const AudioAvatar: FC<{
  sessionId: string;
  photoId?: string;
  avatarConfig: AudioAvatarConfig;
  streaming: boolean;
}> = ({ sessionId, photoId, avatarConfig, streaming }) => {
  const avatarRef = useRef<AudioAvatarService | null>(null);
  const processing = useSelector((state: RootState) => state.avatar.processing);
  const [text, setText] = useState("");
  const [sendAudioMessage] = useSendAudioMessageMutation();
  const { setValue: setProcessing } = useDebounceProcessing();
  const photoIdRef = useRef<string | undefined>();
  const dispatch = useDispatch();

  const speak = useCallback(
    async (newText: string) => {
      setProcessing(true, true);
      setTalking(true);
      console.log(text + newText);
      setText(text + newText);
      avatarRef.current!.speak(text + newText);
      setTalking(false);
      setProcessing(false, true);
    },
    [setProcessing, setText, text],
  );

  const { send } = useCompanionStreamingService(speak);

  const sendMessageStreaming = useCallback(
    async (
      audioBlob?: ArrayBufferLike,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      try {
        setText((text) => text + "<br/>");
        await send({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
        });
      } catch (e) {
        console.error(e);
      }
    },
    [send, sessionId],
  );

  const sendMessageBatching = useCallback(
    async (
      audioBlob?: Blob,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      try {
        const { data } = await sendAudioMessage({
          audio: audioBlob || null,
          sessionId,
          photoId: photoId || null,
          eventType: eventType || null,
        });

        if (!data) {
          return;
        }

        const { aiMessage } = data;

        if (!aiMessage) {
          return;
        }
        await speak(aiMessage);
      } catch (e) {
        console.error(e);
      }
    },
    [sessionId, speak, sendAudioMessage],
  );

  const sendMessage = useCallback(
    async (
      audioBlob?: ArrayBufferLike | Blob,
      photoId?: string,
      eventType?: EventType,
    ): Promise<void> => {
      await (streaming
        ? sendMessageStreaming(audioBlob as ArrayBufferLike, photoId, eventType)
        : sendMessageBatching(audioBlob as Blob, photoId, eventType));
    },
    [streaming, sendMessageStreaming, sendMessageBatching],
  );

  useRecordVoice(
    processing,
    (audio) => sendMessage(audio, photoIdRef.current, EventType.UserInput),
    streaming,
  );

  const startAvatar = async (avatar: AudioAvatarService) => {
    dispatch(setInitializing(true));
    avatar.start();
    await sendMessage(undefined, photoId, EventType.NewConversation);
    dispatch(setInitializing(false));
    dispatch(setReady(true));
  };

  useEffect(() => {
    if (photoId && photoIdRef.current && photoId !== photoIdRef.current) {
      sendMessage(undefined, photoId, EventType.NewPhoto);
    }
    photoIdRef.current = photoId;
  }, [photoId, sendMessage]);

  useEffect(() => {
    setProcessing(true, true);
    const avatar = new AudioAvatarService();
    avatarRef.current = avatar;
    startAvatar(avatar);

    return () => {
      avatar.stop();
      dispatch(setReady(false));
    };
  }, [avatarConfig, setProcessing]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!avatarRef.current) {
    return <Loader />;
  }

  return (
    <AvatarContainer>
      <AvatarImage src={`${process.env.PUBLIC_URL}/images/avatar.png`} />
      <Box
        maxWidth="500px"
        marginTop="16px"
        dangerouslySetInnerHTML={{ __html: text }}
      ></Box>
    </AvatarContainer>
  );
};

export default memo(AudioAvatar);
